import { lazy, Suspense } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import rehypeSlug from 'rehype-slug';
import { VideoViewer } from '@/components/viewers/VideoViewer';
import { ClientOnly } from '@/components/ClientOnly';

// react-pdf pulls in pdf.js which expects browser globals at module load.
// Lazy-load so the SSR bundle never imports it. ClientOnly wraps it so the
// inner Suspense boundary is never reached on the server (renderToString
// can't finish Suspense and would bail up to the nearest enclosing boundary).
const PDFViewer = lazy(() =>
  import('@/components/PDFViewer').then((m) => ({ default: m.PDFViewer })),
);
import { CardGrid } from '@/components/viewers/CardGrid';
import { NeraInlineViewer } from '@/components/viewers/NeraInlineViewer';
import { DecisionTreeInlineViewer } from '@/components/viewers/DecisionTreeInlineViewer';
import { DownloadPDFButton } from '@/components/markdown/DownloadPDFButton';
import { BackToParentButton } from '@/components/markdown/BackToParentButton';
import { remarkVocabulary, remarkHtmlBreaks, vocabularyComponents } from '@/lib/markdown-vocabulary';
import { CARLORBIZ_PROSE } from '@/lib/carlorbiz-prose';

interface TabLike {
  label?: string;
  content_type: string;
  file_url: string | null;
  content: string;
  description?: string | null;
  page_slug?: string | null;
  gamma_id?: string | null;
}

export function TabContentRenderer({ tab }: { tab: TabLike }) {
  const descriptionBlock = tab.description ? (
    <div className={`${CARLORBIZ_PROSE} mb-8`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{tab.description}</ReactMarkdown>
    </div>
  ) : null;

  // Crawler-readable body content for non-insights detail pages. Renders the
  // existing tabs.content markdown below the embed/viewer so Services and
  // About-Me detail pages have substantive HTML body text, not just a navbar
  // and a PDF placeholder. Insights tabs use the rich vocabulary branch above
  // and don't reach this.
  const supplementaryContentBlock =
    tab.content && tab.content.length > 200 ? (
      <div className={`${CARLORBIZ_PROSE} mt-12`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]}>
          {tab.content}
        </ReactMarkdown>
      </div>
    ) : null;

  // Rich vocabulary branch. Triggered either by the presence of a `gamma_id`
  // (set on first Gamma import) or by content that already contains the
  // directive vocabulary (`:::callout`, `:::columns`, `::::cards`). The latter
  // lets hand-authored Services/About-Me tabs use the same rich rendering
  // without being wired to the Gamma re-import pipeline — i.e. they keep their
  // bespoke formatting and won't be overwritten by `/gamma-import all`.
  const usesVocabulary =
    !!tab.content && /(^|\n):{3,}(callout|columns|cards|card)\b/.test(tab.content);
  if ((tab.gamma_id || usesVocabulary) && tab.content && tab.content.length > 200) {
    return (
      <>
        {tab.file_url && (
          <DownloadPDFButton url={tab.file_url} label={tab.label} placement="top" />
        )}
        <div className={CARLORBIZ_PROSE}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkDirective, remarkVocabulary, remarkHtmlBreaks]}
            rehypePlugins={[rehypeSlug]}
            components={vocabularyComponents}
          >
            {tab.content}
          </ReactMarkdown>
        </div>
        {tab.page_slug && <BackToParentButton pageSlug={tab.page_slug} />}
      </>
    );
  }

  switch (tab.content_type) {
    case 'video':
      return (
        <>
          {descriptionBlock}
          {tab.file_url ? (
            <VideoViewer url={tab.file_url} />
          ) : (
            <p className="text-sm text-muted-foreground italic">Video URL not yet configured.</p>
          )}
          {supplementaryContentBlock}
        </>
      );
    case 'pdf':
      return (
        <>
          {descriptionBlock}
          {tab.file_url ? (
            /gamma\.app\/embed|docs\.google\.com\/presentation|canva\.com\/design/.test(tab.file_url) ? (
              <div className="w-full rounded-lg overflow-hidden border" style={{ aspectRatio: '16/9' }}>
                <iframe
                  src={tab.file_url}
                  className="w-full h-full"
                  style={{ border: 'none', minHeight: '500px' }}
                  allow="fullscreen"
                  loading="lazy"
                  title={tab.label || 'Document'}
                />
              </div>
            ) : (
              <ClientOnly
                fallback={
                  <div className="w-full bg-gray-100 rounded-lg" style={{ aspectRatio: '16/9' }} />
                }
              >
                <Suspense
                  fallback={
                    <div className="w-full bg-gray-100 animate-pulse rounded-lg" style={{ aspectRatio: '16/9' }} />
                  }
                >
                  <PDFViewer fileUrl={tab.file_url} title="Document" />
                </Suspense>
              </ClientOnly>
            )
          ) : (
            <p className="text-sm text-muted-foreground italic">PDF not yet uploaded.</p>
          )}
          {supplementaryContentBlock}
        </>
      );
    case 'cards':
      return (
        <>
          {descriptionBlock}
          {tab.file_url ? (
            <CardGrid data={tab.file_url} />
          ) : (
            <p className="text-sm text-muted-foreground italic">Card data not yet configured.</p>
          )}
        </>
      );
    case 'nera':
      return (
        <div className="space-y-0">
          {tab.description && (
            <div className={`${CARLORBIZ_PROSE} mb-6`}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{tab.description}</ReactMarkdown>
            </div>
          )}

          <div className="space-y-8">
            <div>
              <h3 className="font-heading text-xl font-bold text-[#2D7E32] mb-1">Knowledge Q&amp;A</h3>
              <p className="text-sm text-gray-500 mb-4">Ask Nera about Carlorbiz services, methodology, or pricing — she draws on the same knowledge base we build for clients.</p>
              <NeraInlineViewer intro={null} />
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-[#D5B13A]/40 to-transparent" />

            <div>
              <h3 className="font-heading text-xl font-bold text-[#2D7E32] mb-1">Conversational Survey</h3>
              <p className="text-sm text-gray-500 mb-4">Unlike a survey, Nera doesn&rsquo;t ask everyone the same questions. See what a conversation captures that a 1–5 rating scale never will.</p>
              <NeraInlineViewer
                intro={null}
                campaignSlug="carlorbiz-services-fit-v1"
              />
            </div>

            <div className="rounded-xl border-2 border-[#D5B13A]/30 bg-gradient-to-br from-[#D5B13A]/5 to-transparent p-6 space-y-4">
              <p className="text-sm text-foreground leading-relaxed italic">
                &ldquo;Unlike a survey, Nera doesn&rsquo;t ask everyone the same questions. She listens to what you actually say &mdash; and follows that thread. Every conversation is different. Every insight card reflects you.&rdquo;
              </p>
              <p className="text-xs text-muted-foreground">
                That was a fixed-path conversation. The full version follows your thread &mdash; three scenarios, branching questions, personalised insight cards.
              </p>
              <a
                href="/nera-demo"
                className="inline-flex items-center gap-2 bg-[#2D7E32] text-white font-semibold px-6 py-2.5 rounded-full hover:opacity-90 transition-opacity text-sm"
              >
                Try the full experience →
              </a>
            </div>
          </div>
        </div>
      );
    case 'decision-tree':
      return (
        <>
          {descriptionBlock}
          <DecisionTreeInlineViewer slug={tab.content} />
        </>
      );
    case 'text':
    default:
      return tab.content ? (
        <div className={CARLORBIZ_PROSE}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{tab.content}</ReactMarkdown>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">Content coming soon.</p>
      );
  }
}
